import { useSyncExternalStore } from 'react';
import type { Bar, Timeframe } from '../protocol/types';

type Listener = () => void;

const EMPTY_SERIES: Bar[] = Object.freeze([]) as unknown as Bar[];

class ChartStore {
  private series = new Map<string, Bar[]>();
  private listeners = new Map<string, Set<Listener>>();

  key(symbol: string, timeframe: Timeframe): string {
    return `${normalizeSymbol(symbol)}:${timeframe}`;
  }

  setSnapshot(symbol: string, timeframe: Timeframe, bars: Bar[]): void {
    const key = this.key(symbol, timeframe);
    const normalizedBars = freezeBars(bars);

    this.series.set(key, normalizedBars);
    this.emit(key);
  }

  appendBar(bar: Bar, limit = 300): void {
    const normalizedBar = freezeBar({
      ...bar,
      symbol: normalizeSymbol(bar.symbol)
    });

    const key = this.key(normalizedBar.symbol, normalizedBar.timeframe);
    const previous = this.series.get(key) ?? EMPTY_SERIES;
    const next = freezeBars([...previous.slice(-(limit - 1)), normalizedBar]);

    this.series.set(key, next);
    this.emit(key);
  }

  getSeries = (symbol: string, timeframe: Timeframe): Bar[] => {
    return this.series.get(this.key(symbol, timeframe)) ?? EMPTY_SERIES;
  };

  subscribe(symbol: string, timeframe: Timeframe, listener: Listener): () => void {
    const key = this.key(symbol, timeframe);

    let listeners = this.listeners.get(key);

    if (!listeners) {
      listeners = new Set();
      this.listeners.set(key, listeners);
    }

    listeners.add(listener);

    return () => {
      listeners?.delete(listener);

      if (listeners && listeners.size === 0) {
        this.listeners.delete(key);
      }
    };
  }

  private emit(key: string): void {
    this.listeners.get(key)?.forEach((listener) => listener());
  }
}

function freezeBar(bar: Bar): Bar {
  return Object.freeze(bar);
}

function freezeBars(bars: Bar[]): Bar[] {
  return Object.freeze(
    bars.map((bar) =>
      freezeBar({
        ...bar,
        symbol: normalizeSymbol(bar.symbol)
      })
    )
  ) as Bar[];
}

function normalizeSymbol(symbol: string): string {
  return symbol.trim().toUpperCase();
}

export const chartStore = new ChartStore();

export function useBarSeries(symbol: string, timeframe: Timeframe): Bar[] {
  const normalizedSymbol = normalizeSymbol(symbol);

  return useSyncExternalStore(
    (listener) => chartStore.subscribe(normalizedSymbol, timeframe, listener),
    () => chartStore.getSeries(normalizedSymbol, timeframe),
    () => chartStore.getSeries(normalizedSymbol, timeframe)
  );
}